import type { NavigateFunction } from 'react-router-dom';
import { routes } from '../routes/routePaths';

export const DASHBOARD_KEYS = {
  origination: 'origination',
  risk: 'risk',
  committee: 'committee',
  instrumentation: 'instrumentation',
  finishedFolders: 'finishedFolders',
  clients: 'clients',
} as const;

export type DashboardKey = (typeof DASHBOARD_KEYS)[keyof typeof DASHBOARD_KEYS];

export type ListNavigationState = {
  dashboardKey: DashboardKey;
  page: number;
  searchText: string;
  activeFilters: Record<string, string>;
  selectedIds?: string[];
  selectedApplications?: string[];
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
};

export type DetailLocationState = {
  listState?: ListNavigationState;
  status?: string;
  statusCode?: string;
  referrer_name?: string;
  fromSolicitudesTab?: boolean;
  isReadOnly?: boolean;
  [key: string]: unknown;
};

const DASHBOARD_ROUTE_MAP: Record<DashboardKey, string> = {
  [DASHBOARD_KEYS.origination]: routes.dashboard,
  [DASHBOARD_KEYS.risk]: routes.risksAnalysis,
  [DASHBOARD_KEYS.committee]: routes.committee,
  [DASHBOARD_KEYS.instrumentation]: routes.instrumentation,
  [DASHBOARD_KEYS.finishedFolders]: routes.finishedFolders,
  [DASHBOARD_KEYS.clients]: routes.clientsList,
};

export function resolveListRoute(key: DashboardKey): string {
  return DASHBOARD_ROUTE_MAP[key];
}

export function selectedIdsToWire(set: Set<number>): string[] {
  return [...set].map(String);
}

export function selectedIdsFromWire(ids?: string[]): Set<number> {
  return new Set(
    (ids ?? [])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id)),
  );
}

export function selectedApplicationsToWire(set: Set<number>): string[] {
  return selectedIdsToWire(set);
}

export function selectedApplicationsFromWire(ids?: string[]): Set<number> {
  return selectedIdsFromWire(ids);
}

export function buildListState(input: {
  dashboardKey: DashboardKey;
  page: number;
  searchText: string;
  activeFilters: Record<string, string>;
  selectedIds?: Set<number>;
  selectedApplications?: Set<number>;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
}): ListNavigationState {
  const state: ListNavigationState = {
    dashboardKey: input.dashboardKey,
    page: input.page,
    searchText: input.searchText,
    activeFilters: { ...input.activeFilters },
  };

  if (input.selectedIds && input.selectedIds.size > 0) {
    state.selectedIds = selectedIdsToWire(input.selectedIds);
  }

  if (input.selectedApplications && input.selectedApplications.size > 0) {
    state.selectedApplications = selectedApplicationsToWire(input.selectedApplications);
  }

  if (input.sortField) {
    state.sortField = input.sortField;
    state.sortDirection = input.sortDirection ?? null;
  }

  return state;
}

function isDashboardKey(value: unknown): value is DashboardKey {
  return (
    typeof value === 'string' &&
    Object.values(DASHBOARD_KEYS).includes(value as DashboardKey)
  );
}

function isRecord(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every((entry) => typeof entry === 'string');
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

export function extractListStateFromLocation(
  state: unknown,
  expectedDashboardKey: DashboardKey,
): ListNavigationState | null {
  if (typeof state !== 'object' || state === null) {
    return null;
  }

  const listState = (state as { listState?: unknown }).listState;
  if (typeof listState !== 'object' || listState === null) {
    return null;
  }

  const candidate = listState as Partial<ListNavigationState>;

  if (!isDashboardKey(candidate.dashboardKey)) {
    return null;
  }

  if (candidate.dashboardKey !== expectedDashboardKey) {
    return null;
  }

  if (typeof candidate.page !== 'number' || !Number.isFinite(candidate.page)) {
    return null;
  }

  if (typeof candidate.searchText !== 'string') {
    return null;
  }

  if (!isRecord(candidate.activeFilters)) {
    return null;
  }

  const restored: ListNavigationState = {
    dashboardKey: candidate.dashboardKey,
    page: candidate.page,
    searchText: candidate.searchText,
    activeFilters: { ...candidate.activeFilters },
  };

  if (candidate.selectedIds !== undefined) {
    if (!isStringArray(candidate.selectedIds)) {
      return null;
    }
    restored.selectedIds = [...candidate.selectedIds];
  }

  if (candidate.selectedApplications !== undefined) {
    if (!isStringArray(candidate.selectedApplications)) {
      return null;
    }
    restored.selectedApplications = [...candidate.selectedApplications];
  }

  if (candidate.sortField !== undefined && candidate.sortField !== null) {
    if (typeof candidate.sortField !== 'string') {
      return null;
    }
    restored.sortField = candidate.sortField;
  }

  if (candidate.sortDirection !== undefined && candidate.sortDirection !== null) {
    if (candidate.sortDirection !== 'asc' && candidate.sortDirection !== 'desc') {
      return null;
    }
    restored.sortDirection = candidate.sortDirection;
  }

  return restored;
}

export function navigateBackToList(
  navigate: NavigateFunction,
  listState: ListNavigationState | undefined,
  fallbackRoute: string,
): void {
  const route = listState ? resolveListRoute(listState.dashboardKey) : fallbackRoute;

  if (listState) {
    navigate(route, { state: { listState } });
    return;
  }

  navigate(route);
}

const PATH_DASHBOARD_RULES: ReadonlyArray<{
  pattern: RegExp;
  key: DashboardKey | null;
}> = [
  { pattern: /^\/clientes\/list$/, key: DASHBOARD_KEYS.clients },
  { pattern: /^\/clientes\/[^/]+\/applications\/[^/]+$/, key: DASHBOARD_KEYS.origination },
  { pattern: /^\/clientes\/[^/]+\/application\/[^/]+$/, key: null },
  { pattern: /^\/analisisderiesgo(?:\/|$)/, key: DASHBOARD_KEYS.risk },
  { pattern: /^\/comite(?:\/|$)/, key: DASHBOARD_KEYS.committee },
  { pattern: /^\/instrumentacion(?:\/|$)/, key: DASHBOARD_KEYS.instrumentation },
  { pattern: /^\/carpetas-finalizadas(?:\/|$)/, key: DASHBOARD_KEYS.finishedFolders },
  { pattern: /^\/clientes\/[^/]+$/, key: DASHBOARD_KEYS.clients },
  { pattern: /^\/clientes$/, key: DASHBOARD_KEYS.origination },
  { pattern: /^\/inicio$/, key: DASHBOARD_KEYS.origination },
];

export function resolveDashboardKeyFromPath(pathname: string): DashboardKey | null {
  for (const rule of PATH_DASHBOARD_RULES) {
    if (rule.pattern.test(pathname)) {
      return rule.key;
    }
  }
  return null;
}

export function navigateAfterFlowAction(
  navigate: NavigateFunction,
  pathname: string,
  locationState: DetailLocationState | undefined | null,
): void {
  const listState = locationState?.listState;

  if (listState) {
    navigateBackToList(navigate, listState, resolveListRoute(listState.dashboardKey));
    return;
  }

  if (locationState?.fromSolicitudesTab === true) {
    navigate(resolveListRoute(DASHBOARD_KEYS.clients));
    return;
  }

  const pathKey = resolveDashboardKeyFromPath(pathname);
  if (pathKey) {
    navigate(resolveListRoute(pathKey));
    return;
  }

  navigate(resolveListRoute(DASHBOARD_KEYS.origination));
}
